import { I18n } from 'react-redux-i18n-lite';
import { ACTIVITY_TYPE, ASSET_TYPES, FILE_TYPES, PARAMS } from '../../const';
import { API_CLASSROOMS, API_BACKOFFICE_ASSETS } from '../api/apiConst';
import * as activitiesApi from '../api/activitiesApi';
import * as bookApi from '../api/bookApi';
import * as CustomTranslate from '../translate/customTranslateService';
import axios from 'axios';
import * as shared from '../../shared';
import { Cmi5 } from '../cmi5/cmi5';
import { findSpecialCharacters, removeDiacriticsWithoutSpaces } from '../../utils/stringUtils';
import { getMimetypeFromFile } from '../mimetypes/mimetypesService';
import * as assetsService from '../assets/assetsService';
import { ANALYTICS_CONTEXT } from '../../types/analytics';

export const getBookNameFromBookActivity = book => {
  if (book.name) {
    const name = JSON.parse(book.name);
    const locale = Object.keys(name);
    return name[locale[0]];
  }
  return I18n.t('classroom.bookDefaultName');
};

export const isLinkActivity = activityFormat => activityFormat === FILE_TYPES.LNK.mimeType;

export const isPdfActivity = activityFormat => activityFormat === 'application/pdf';

export const isPdfOrLinkActivity = activityFormat =>
  isLinkActivity(activityFormat) || isPdfActivity(activityFormat);

export const isValidUrl = url => {
  const urlRegex = /^(https?|ftp):\/\/([a-zA-Z0-9.-]+(:[a-zA-Z0-9.&%$-]+)*@)*((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.(com|edu|gov|int|mil|net|org|biz|arpa|info|name|cloud|pro|aero|coop|museum|[a-zA-Z]{2}))(:[0-9]+)*(\/($|[a-zA-Z0-9.,?'\\+&%$#=~_-]+))*$/;
  return urlRegex.test(url);
};

export const removeNotAllowedCharactersToActivityId = activityId => {
  if (!activityId) return activityId;

  const NOT_ALLOWED_REGEXPS = [/\//g, /\./g, /:/g];

  NOT_ALLOWED_REGEXPS.forEach(regexp => {
    activityId = activityId.toString().replace(regexp, '');
  });

  return activityId;
};

export const addLessonToCMS = function(lessonName, url, format, idClassroom, unitActivityId, context) {
  const payload = {
    language: shared.getLanguageFromStorage(),
    name: lessonName,
    type: ACTIVITY_TYPE.LESSON,
    url: url,
    format: format
  };

  if (context) {
    payload.context = context;
  }

  return activitiesApi.postActivityTree(idClassroom, unitActivityId, payload);
};

export async function addDocLesson(
  fileToUpload,
  userId,
  customerId,
  idClassroom,
  unitActivityId,
  handleUploadContentStatus
) {
  let uploadedFile;
  let isFileTooBig;
  let isNameValid;

  if (fileToUpload.length) {
    const file = fileToUpload[0];
    const finalFileName = removeDiacriticsWithoutSpaces(file.name);
    // 105000000 bytes ~= 100 MB dado Math.round(bytes / Math.pow(1024, 2), 2)
    isFileTooBig = file.size > 105000000;
    if (isFileTooBig) return { uploadedFile: null, isFileTooBig, isNameValid };
    isNameValid = findSpecialCharacters([finalFileName]);
    if (!isNameValid) return { uploadedFile: null, isFileTooBig, isNameValid };
    const assetMetadata = {
      fileName: finalFileName,
      userId,
      customerId,
      assetType: ASSET_TYPES.LESSON
    };
    try {
      uploadedFile = await assetsService.uploadAsset(file, assetMetadata, { handleUploadContentStatus });
      const url = API_BACKOFFICE_ASSETS.replace(PARAMS.ASSET, uploadedFile.uuid).replace(PARAMS.CUSTOMER, customerId);
      const fileFormat = getMimetypeFromFile(uploadedFile.fileName);
      let status = { isUploading: false };
      handleUploadContentStatus(status);
      addLessonToCMS(uploadedFile.fileName, url, fileFormat, idClassroom, unitActivityId, ANALYTICS_CONTEXT.FILE_UPLOAD);
    } catch (err) {
      return;
    }
  }
  return { uploadedFile, isFileTooBig, isNameValid };
}

export async function addLinkLesson(lessonName, url, classroomId, unitActivityId) {
  return addLessonToCMS(lessonName, url, FILE_TYPES.LNK.mimeType, classroomId, unitActivityId, ANALYTICS_CONTEXT.LINK_ADD);
}

export const transformCmi5ToBook = (cmi5Properties, bookId) => {
  const bookCmi5 = new Cmi5(cmi5Properties, bookId);
  return bookCmi5.toBookActivity();
};

export const desynchronizeBook = (classroomId, bookId) => {
  const payload = {
    synchronized: false
  };

  return axios.patch(`${API_CLASSROOMS}/${classroomId}/books/${bookId}`, payload);
};

export const propagateBookAssignations = book => {
  const bookIsAssigned = book.configuration.users.length || book.configuration.groups.length;
  book.content.forEach(unit => {
    const unitIsAssigned = unit.configuration.users.length || unit.configuration.groups.length;
    if (!unitIsAssigned && bookIsAssigned) {
      unit.configuration = book.configuration;
    }
    unit.content.forEach(lesson => {
      const lessonIsAssigned = lesson.configuration.users.length || lesson.configuration.groups.length;
      if (!lessonIsAssigned) {
        lesson.configuration = unit.configuration;
      }
    });
  });
};

/**
 * Clones a lesson to the same unit or another unit
 * @param {Object} lesson - The lesson object to clone
 * @param {string} classroomId - The classroom ID
 * @param {string} bookId - The book ID
 * @param {string} unitActivityId - The target unit activity ID (where to clone)
 * @returns {Promise}
 */
export const cloneLesson = async (lessonActivityId, importFrom, classroomId, bookId, unitActivityId, context) => {
  if (!lessonActivityId) {
    console.error('Lesson and activityId are required');
    throw new Error('Lesson and activityId are required');
  }

    const payload = {
    classId: classroomId,
    bookId: bookId,
    unitActivityId: unitActivityId,
    lessonActivityId: lessonActivityId,
  };
  
  // Solo incluir importFrom si está definido (no null ni undefined)
  // El backend rechaza importFrom cuando se clona dentro del mismo libro
  if (importFrom !== null && importFrom !== undefined) {
    payload.importFrom = importFrom;
  }
  
  if (context) {
    payload.context = context;
  }
  return bookApi.cloneLessonActivity(payload);
};
